import { DATE_FORMAT } from '@/helpers/dates';
import { Box, CheckboxInput, DateInput } from '@palmetto/palmetto-components';
import { useFormikContext } from 'formik';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';

import { ActivationDocumentTypes, AllDocumentTypes } from 'types';

import FormSection from '../Forms/FormSection';
import { DocumentList } from '../MilestonePackagePartials/Documents/DocumentList';

import { PTOProofReview } from './PTOProofReview';

interface ProofOfPtoProps {
  showSubmit?: boolean;
  disabled?: boolean;
  onSubmit?: () => void;
  showCheckboxes?: boolean;
  checkboxCategories?: AllDocumentTypes[];
  isReviewMode?: boolean;
  isPackageEditable?: boolean;
}

export const ProofOfPto = ({
  disabled = false,
  showCheckboxes = false,
  checkboxCategories = [],
  isReviewMode = false,
  isPackageEditable = true,
}: ProofOfPtoProps) => {
  const { values, isSubmitting, errors, touched, setFieldValue } = useFormikContext<any>();
  const [isEpcPtoDateIncorrect, setIsEpcPtoDateIncorrect] = useState(false);
  const [originalPtoDate, setOriginalPtoDate] = useState(values.ptoGrantedDate);
  const utilityLseId = values.lseId;

  // Reset state when form reinitializes (after save)
  useEffect(() => {
    if (values.qcCorrectedPtoDate === undefined && isEpcPtoDateIncorrect) {
      setIsEpcPtoDateIncorrect(false);
    }
    // Update originalPtoDate when ptoGrantedDate changes from external source
    if (values.ptoGrantedDate && values.ptoGrantedDate !== originalPtoDate && !isEpcPtoDateIncorrect) {
      setOriginalPtoDate(values.ptoGrantedDate);
    }
  }, [
    values.ptoGrantedDate,
    values.qcCorrectedPtoDate,
    originalPtoDate,
    isEpcPtoDateIncorrect,
    setIsEpcPtoDateIncorrect,
    setOriginalPtoDate,
  ]);
  const netMeteringDocRequiredLseId = 2241;
  const minDate = DateTime.fromISO('2022-01-01').toJSDate();
  const maxDate = DateTime.local().endOf('day').toJSDate();
  const ptoGrantedDate = values.ptoGrantedDate
    ? DateTime.fromFormat(values.ptoGrantedDate, DATE_FORMAT).toJSDate()
    : null;
  const handleDateChange = useCallback(
    (date: Date | [Date, Date] | null) => {
      if (!date || !(date instanceof Date)) return;
      if (!disabled) {
        setFieldValue('ptoGrantedDate', DateTime.fromJSDate(date).toLocal().toFormat(DATE_FORMAT));
      }
    },
    [disabled, setFieldValue],
  );

  return (
    <FormSection
      title="Proof of Permission to Operate"
      description="Utility PTO proof such as letter/email copy, or screenshot of utility portal status"
    >
      <PTOProofReview isPTODateSubmitting={isSubmitting} />
      <DocumentList
        documentTypeFilter={[ActivationDocumentTypes.ptoConfirmation]}
        title="Proof of PTO Documents"
        showTypeLabelOnItems={false}
        showDocumentActions={!disabled || isSubmitting}
        showUploadButton={!disabled || isSubmitting}
        baseCategory={ActivationDocumentTypes.ptoConfirmation}
        showCheckboxes={showCheckboxes}
        checkboxCategories={checkboxCategories}
        isPackageEditable={isPackageEditable}
      />
      <Box childGap="md">
        <DateInput
          label={isReviewMode && isEpcPtoDateIncorrect ? 'EPC Submitted PTO Granted Date' : 'PTO Granted Date'}
          name="ptoGrantedDate"
          id="ptoGrantedDate"
          placeholder={'Pick a date'}
          dateFormat={DATE_FORMAT}
          isDisabled={disabled || isSubmitting || isReviewMode || !isPackageEditable}
          width="2xl"
          value={values.ptoGrantedDate}
          error={touched.ptoGrantedDate && errors.ptoGrantedDate ? errors.ptoGrantedDate : undefined}
          datePickerProps={{
            maxDate,
            minDate,
            onChange: handleDateChange,
            openToDate: ptoGrantedDate ?? undefined,
            isDisabled: disabled || isSubmitting || isReviewMode || !isPackageEditable,
          }}
          textInputProps={
            isReviewMode || disabled
              ? {}
              : {
                  onClear: () => setFieldValue('ptoGrantedDate', ''),
                }
          }
        />
        {isReviewMode && (
          <CheckboxInput
            id="isEpcPtoDateIncorrect"
            label="EPC submitted incorrect PTO Granted Date, update needed"
            isChecked={isEpcPtoDateIncorrect}
            isDisabled={disabled || isSubmitting || !isPackageEditable}
            onChange={(event) => {
              const reviewUserWantsToCorrectPTODate = event.target.checked;
              setIsEpcPtoDateIncorrect(reviewUserWantsToCorrectPTODate);
              setFieldValue('isEpcPtoDateIncorrect', reviewUserWantsToCorrectPTODate);
              if (reviewUserWantsToCorrectPTODate) {
                // Store the original date when checkbox is first checked
                setFieldValue('epcIncorrectSubmittedPtoGrantedDate', originalPtoDate);
                setFieldValue('qcCorrectedPtoDate', '');
              } else {
                // Clear the stored original date if unchecked
                setFieldValue('epcIncorrectSubmittedPtoGrantedDate', undefined);
                setFieldValue('qcCorrectedPtoDate', undefined);
              }
            }}
          />
        )}
        {isReviewMode && isEpcPtoDateIncorrect && (
          <DateInput
            label="QC Corrected PTO Date"
            name="qcCorrectedPtoDate"
            id="qcCorrectedPtoDate"
            placeholder={'Pick a date'}
            dateFormat={DATE_FORMAT}
            isDisabled={disabled || isSubmitting || !isPackageEditable}
            width="2xl"
            value={values.qcCorrectedPtoDate || ''}
            error={touched.qcCorrectedPtoDate && errors.qcCorrectedPtoDate ? errors.qcCorrectedPtoDate : undefined}
            datePickerProps={{
              maxDate,
              minDate,
              onChange: (date: Date | [Date, Date] | null) => {
                if (!date || !(date instanceof Date)) return;
                setFieldValue('qcCorrectedPtoDate', DateTime.fromJSDate(date).toLocal().toFormat(DATE_FORMAT));
              },
              openToDate: values.qcCorrectedPtoDate
                ? DateTime.fromFormat(values.qcCorrectedPtoDate, DATE_FORMAT).toJSDate()
                : undefined,
            }}
            textInputProps={{
              onClear: () => setFieldValue('qcCorrectedPtoDate', ''),
            }}
          />
        )}
      </Box>
      {utilityLseId === netMeteringDocRequiredLseId && (
        <DocumentList
          documentTypeFilter={[ActivationDocumentTypes.netMeteringApproval]}
          title="Net Metering Approval (COMED Only)"
          showTypeLabelOnItems={false}
          baseCategory={ActivationDocumentTypes.ptoConfirmation}
          showCheckboxes={showCheckboxes}
          checkboxCategories={checkboxCategories}
          isPackageEditable={isPackageEditable}
        />
      )}
    </FormSection>
  );
};
